import {
  Box,
  Button,
  Checkbox,
  CopyToClipboard,
  DatePicker,
  FormField,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-console";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EnumeratedIconikAsset } from "@/components/iconik/types";

import useModal from "../useModal";
import useShareClip from "./useShareClip";
import styles from "./useShareClipModal.module.css";

type ShareMode = "single" | "multiple";

/** Today at local midnight — used to disable past dates in the picker. */
const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Days ahead the expiry picker defaults to. Kept well within the server-side
 * maximum (``MAX_SHARE_LINK_EXPIRY_DAYS``, currently 7) so the default value
 * is always accepted by ``POST /share`` — a longer default would be rejected.
 */
const DEFAULT_EXPIRY_DAYS = 3;

/** ``YYYY-MM-DD`` ``DEFAULT_EXPIRY_DAYS`` from today (default link expiry). */
const defaultExpiry = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + DEFAULT_EXPIRY_DAYS);
  return d.toISOString().slice(0, 10);
};

/**
 * Drives the Clip Preview "Share" flow as a modal (consistent with Export).
 *
 * - ``openSingle(clip)`` shares one clip: a single settings screen
 *   (download access + expiry) that generates a public Iconik share URL.
 * - ``openMultiple(clips)`` is a two-step flow scoped to the accordion's
 *   clips: step 1 selects which clips, step 2 is the same settings screen.
 *   Sharing multiple assets returns one URL pointing at an Iconik
 *   share-collection.
 *
 * Every clip is shareable (a share can serve the proxy), so — unlike export —
 * no clips are excluded from the selection list.
 */
const useShareClipModal = () => {
  const { createShare, loading, error } = useShareClip();

  const [mode, setMode] = useState<ShareMode>("single");
  // Single share is one screen (settings). Multiple starts on the
  // clip-selection screen (step 1) then advances to the settings (step 2).
  const [step, setStep] = useState<1 | 2>(2);
  const [candidates, setCandidates] = useState<EnumeratedIconikAsset[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(
    new Set()
  );
  const [singleTitle, setSingleTitle] = useState<string | undefined>(undefined);
  const [allowHighRes, setAllowHighRes] = useState(true);
  const [allowProxy, setAllowProxy] = useState(true);
  const [expiryDate, setExpiryDate] = useState<string>(defaultExpiry());
  const [shareUrl, setShareUrl] = useState("");

  const nothingSelected = !allowHighRes && !allowProxy;
  const noExpiry = !expiryDate;

  // Any change that affects the link invalidates an already-generated URL.
  const clearUrl = () => setShareUrl("");

  const resolvedAssetIds = useMemo(() => {
    if (mode === "single") {
      return candidates.map((clip) => clip.assetId);
    }
    return candidates
      .filter((clip) => selectedAssetIds.has(clip.assetId))
      .map((clip) => clip.assetId);
  }, [mode, candidates, selectedAssetIds]);

  const toggleAsset = (assetId: string, checked: boolean) => {
    clearUrl();
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(assetId);
      } else {
        next.delete(assetId);
      }
      return next;
    });
  };

  const generate = useCallback(async () => {
    if (nothingSelected || noExpiry || resolvedAssetIds.length === 0) {
      return;
    }
    const url = await createShare({
      assetIds: resolvedAssetIds,
      allowDownload: allowHighRes,
      allowDownloadProxy: allowProxy,
      // Only a single share carries the clip's filename; a multi-clip share
      // lets the backend default the collection title.
      title: mode === "single" ? singleTitle : undefined,
      expires: `${expiryDate}T00:00:00.000Z`,
    });
    if (url) {
      setShareUrl(url);
    }
  }, [
    mode,
    resolvedAssetIds,
    allowHighRes,
    allowProxy,
    singleTitle,
    expiryDate,
    nothingSelected,
    noExpiry,
    createShare,
  ]);

  const onSettingsScreen = mode === "single" || step === 2;
  const canShare =
    !nothingSelected && !noExpiry && resolvedAssetIds.length > 0;
  const canAdvance = resolvedAssetIds.length > 0;

  const settingsScreen = (
    <SpaceBetween size="m">
      <FormField
        label="Recipient access"
        description="Choose what the recipient can download."
      >
        <SpaceBetween size="xs">
          <Checkbox
            checked={allowHighRes}
            onChange={({ detail }) => {
              setAllowHighRes(detail.checked);
              clearUrl();
            }}
          >
            Allow high-res download
          </Checkbox>
          <Checkbox
            checked={allowProxy}
            onChange={({ detail }) => {
              setAllowProxy(detail.checked);
              clearUrl();
            }}
          >
            Allow proxy download
          </Checkbox>
        </SpaceBetween>
      </FormField>
      {nothingSelected && (
        <StatusIndicator type="warning">
          Allow at least one download type.
        </StatusIndicator>
      )}
      <FormField
        label="Link expiry"
        description="The share link stops working after this date."
      >
        <DatePicker
          value={expiryDate}
          onChange={({ detail }) => {
            setExpiryDate(detail.value);
            clearUrl();
          }}
          isDateEnabled={(date) => date > startOfToday()}
          placeholder="YYYY/MM/DD"
          openCalendarAriaLabel={(selectedDate) =>
            "Choose expiry date" +
            (selectedDate ? `, selected date is ${selectedDate}` : "")
          }
        />
      </FormField>
      {error && <StatusIndicator type="error">{error}</StatusIndicator>}
      {shareUrl && (
        <FormField label="Share link">
          <CopyToClipboard
            copyButtonAriaLabel="Copy share link"
            copySuccessText="Copied!"
            copyErrorText="Failed to copy"
            textToCopy={shareUrl}
            variant="inline"
          />
        </FormField>
      )}
    </SpaceBetween>
  );

  const selectionScreen = (
    <SpaceBetween size="xs">
      <Box>Select the clips to share.</Box>
      <div className={styles.clipList} data-testid="share-clip-list">
        {candidates.map((clip) => (
          <label key={clip.assetId} className={styles.clipRow}>
            <Checkbox
              checked={selectedAssetIds.has(clip.assetId)}
              onChange={({ detail }) => toggleAsset(clip.assetId, detail.checked)}
            />
            <span className={styles.clipName} title={clip.filename}>
              {clip.filename}
            </span>
          </label>
        ))}
      </div>
    </SpaceBetween>
  );

  const generateButton = (
    <Button
      variant="primary"
      onClick={generate}
      loading={loading}
      disabled={!canShare}
    >
      {shareUrl ? "Regenerate link" : "Generate link"}
    </Button>
  );

  const additionalActions = onSettingsScreen ? (
    mode === "multiple" ? (
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="normal" onClick={() => setStep(1)}>
          Back
        </Button>
        {generateButton}
      </SpaceBetween>
    ) : (
      generateButton
    )
  ) : (
    <Button variant="normal" onClick={() => setStep(2)} disabled={!canAdvance}>
      Next
    </Button>
  );

  const { visible, toggle, ModalComponent } = useModal({
    title: mode === "single" ? "Share Clip" : "Share Multiple Clips",
    // Generate / Next live in additionalActions so they never close the
    // modal; the footer's Close button dismisses it.
    additionalActions,
    content: onSettingsScreen ? settingsScreen : selectionScreen,
  });

  const resetSettings = () => {
    setAllowHighRes(true);
    setAllowProxy(true);
    setExpiryDate(defaultExpiry());
    setShareUrl("");
  };

  const openSingle = useCallback(
    (clip: EnumeratedIconikAsset) => {
      setMode("single");
      setStep(2);
      setCandidates([clip]);
      setSelectedAssetIds(new Set([clip.assetId]));
      setSingleTitle(clip.filename);
      resetSettings();
      toggle();
    },
    [toggle]
  );

  const openMultiple = useCallback(
    (clips: EnumeratedIconikAsset[]) => {
      setMode("multiple");
      setStep(1);
      setCandidates(clips);
      setSelectedAssetIds(new Set(clips.map((c) => c.assetId)));
      setSingleTitle(undefined);
      resetSettings();
      toggle();
    },
    [toggle]
  );

  useEffect(() => {
    if (!visible) {
      setMode("single");
      setStep(2);
      setCandidates([]);
      setSelectedAssetIds(new Set());
      setShareUrl("");
    }
  }, [visible]);

  return { ModalComponent, openSingle, openMultiple };
};

export default useShareClipModal;
